import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { CatalogItemKind } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../../common/types';
import { useGetCatalogQuery } from '../../../../redux/api/fymAPI';
import { RecentCatalogRow } from './RecentCatalogRow';
import { RecentCatalogItemsProps } from './props';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel and its rows; the row lives in its own file */
import styles from './RecentCatalogItems.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** Five rows. More than that is a catalog, and the catalog has its own screen. */
const RECENT_LIMIT = 5;

/**
 * "Recent in your catalog" — what turns a release the artist already claimed
 * into a page in one tap.
 *
 * The list is read with no filter and no search, the same request the Catalog
 * screen makes, so the two share one cache entry. The narrowing happens here
 * instead: a release page can only start from a claimed row, while a pre-save
 * can start from either kind.
 *
 * An artist with an empty catalog sees nothing at all — the search field below
 * is still the whole flow, exactly as it was.
 */
const RecentCatalogItemsComponent: React.FC<RecentCatalogItemsProps> = ({ accountId, kind, onPick }) => {
  const intl = useIntl();
  const { data } = useGetCatalogQuery({ accountId });

  const items = useMemo(() => {
    const all = data?.items ?? [];
    return all
      .filter((item) => {
        if (kind === undefined) {
          return true;
        }
        // A claimed row with no maid cannot be resolved back to a catalog
        // track, so it would open a wizard with nothing to work on.
        return item.kind === kind && (kind !== CatalogItemKind.claimed || item.catalogTrackMaid !== null);
      })
      .slice(0, RECENT_LIMIT);
  }, [data?.items, kind]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className={styles.panel}>
      <div className={styles.title}>
        {intl.formatMessage({
          id: 'app.sml.wizards.recent_catalog.title',
          defaultMessage: 'Recent in your catalog',
        })}
      </div>
      <div className={styles.list}>
        {items.map((item) => (
          <RecentCatalogRow key={item.uuid} item={item} onPick={onPick} />
        ))}
      </div>
    </div>
  );
};

export const RecentCatalogItems = TypedMemo(RecentCatalogItemsComponent);
